'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FAQItemType } from '@/types/ui';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function FAQ() {
    const t = useTranslations('FAQ');
    const [openId, setOpenId] = useState<string | null>(null);

    const toggleAccordion = (id: string) => {
        setOpenId((prev) => (prev === id ? null : id));
    };

    const col1Items: FAQItemType[] = [
        { id: 'free', question_key: 'question_1', answer_key: 'answer_1' },
        { id: 'safe', question_key: 'question_3', answer_key: 'answer_3' },
        { id: 'international', question_key: 'question_5', answer_key: 'answer_5' },
        { id: 'limits', question_key: 'question_7', answer_key: 'answer_7' },
    ];

    const col2Items: FAQItemType[] = [
        { id: 'need_app', question_key: 'question_2', answer_key: 'answer_2' },
        { id: 'speed', question_key: 'question_4', answer_key: 'answer_4' },
        { id: 'wrong_number', question_key: 'question_6', answer_key: 'answer_6' },
        { id: 'standalone', question_key: 'question_8', answer_key: 'answer_8' },
    ];

    const renderAccordionColumn = (items: FAQItemType[]) => (
        <div className="bg-white rounded-3xl border border-slate-200/80 shadow-sm overflow-hidden divide-y divide-slate-100">
            {items.map((item: FAQItemType) => {
                const isOpen = openId === item.id;
                return (
                    <div key={item.id} className="transition-colors duration-150">
                        <button
                            onClick={() => toggleAccordion(item.id)}
                            className="w-full text-left py-5 px-6 sm:px-8 flex items-center justify-between gap-4 group focus:outline-none"
                            aria-expanded={isOpen}
                        >
                            <span className="text-base sm:text-lg font-bold text-gray-900 tracking-tight leading-snug">
                                {t(item.question_key)}
                            </span>
                            <div className="w-6 h-6 shrink-0 flex items-center justify-center color-primary font-medium text-xl transition-transform duration-300">
                                <span className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                                    {isOpen ? '-' : '+'}
                                </span>
                            </div>
                        </button>
                        <div
                            className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0'
                                }`}
                        >
                            <div className="overflow-hidden px-6 sm:px-8">
                                <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                    {t(item.answer_key)}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <motion.section
            className="w-full bg-white py-16 sm:py-24 px-6 sm:px-10 lg:px-16"
            id="faq"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
        >
            <div className="max-w-8xl mx-auto">
                <div className="mb-10 sm:mb-12">
                    <span className="text-xs sm:text-sm font-bold tracking-widest color-primary uppercase">
                        {t('faq')}
                    </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                    {renderAccordionColumn(col1Items)}
                    {renderAccordionColumn(col2Items)}
                </div>
            </div>
        </motion.section>
    );
}